// Tierzustand und Verhalten: stehen, ein Ziel in der Koppel wählen, hingehen, wieder stehen.
// Reine Logik in festen Schritten (Architektur-Regel 2), ohne Zufall zur Laufzeit: Die Zielwahl
// kommt aus einem Hash von Tier-id und Tick, damit gleicher Startzustand gleiches Ergebnis liefert.
// Kollision wie der Avatar über moveAabb, dazu die Koppel als Grenze, der Avatar als Hindernis
// beim Gehen und das Ausweichen, wenn der Avatar in das Tier hineinläuft. Einzelheiten und
// bewusste Vereinfachungen (kein Fall, Ausweichen nur waagerecht) in ADR 0008; warum das Tier nur
// vom Avatar weg ausweicht und sonst der Avatar stoppt, in ADR 0018.
//
// Zum Import aus ../sim/state.js: state.ts importiert seinerseits createAnimal von hier (ADR 0007).
// Der Kreis ist harmlos, solange diese Datei die Werte aus state.ts nur in Funktionen verwendet,
// nie auf Modulebene; sonst wären sie beim Laden über das Barrel noch nicht initialisiert.

import { aabbAt, aabbsOverlap, moveAabb, separationDelta, type Aabb } from '../sim/physics.js';
import { PLAYER, TICK_RATE, TICK_SECONDS, type PlayerState, type Vec3 } from '../sim/state.js';
import { clamp } from '../util/math.js';
import { isSolid, type World } from '../world/chunk.js';
import type { AnimalSpawn, Pen } from '../world/types.js';
import { SPECIES, getSpecies, type Species } from './species.js';

export type { Aabb } from '../sim/physics.js';

/**
 * Ein Tier im Spielzustand. `pos` ist der Fußpunkt in der Mitte der Grundfläche, `yaw` die
 * Blickrichtung in der Konvention des Spielers (0 = Norden, positiv nach links), `target` das
 * Ziel des aktuellen Gangs oder `null` beim Stehen.
 */
export interface AnimalState {
  id: string;
  species: string;
  pos: Vec3;
  yaw: number;
  target: Vec3 | null;
  /** Verbleibende Ticks Stehzeit, bevor ein neues Ziel gewählt wird. */
  idleTicks: number;
  penId: string;
  /** Tick, ab dem das Produkt (wieder) bereit ist; `<= tick` heißt bereit. */
  productReadyTick: number;
  /** Wahr in Ticks, in denen das Tier tatsächlich vorangekommen ist; steuert die Beinanimation. */
  moving: boolean;
  /** Ticks in Folge, in denen das Tier trotz Ziel kaum vorankam (Ergänzung zum Vertrag, ADR 0008). */
  blockedTicks: number;
}

/** Die Stellschrauben des Verhaltens; Zeiten in Sekunden Simulationszeit, Längen in Blöcken. */
export const ANIMAL_BEHAVIOR = {
  /** Stehzeit zwischen zwei Gängen, ganze Ticks gleichverteilt zwischen min und max. */
  idleMinSeconds: 1,
  idleMaxSeconds: 4,
  /** Unterhalb dieses Abstands zum Ziel gilt das Tier als angekommen. */
  arrivalDistance: 0.1,
  /** Wer so lange kaum vorankommt, gibt das Ziel auf und wählt ein neues. */
  blockedSeconds: 1,
  /** Ein Tick zählt als blockiert, wenn der Abstand zum Ziel um weniger als diesen Anteil des Schritts sinkt. */
  blockedProgressFraction: 0.1,
  /**
   * Kleiner Spalt beim Trennen von Tier und Avatar, damit die Quader sich danach nicht berühren;
   * gilt für das Ausweichen des Tiers wie für den Stopp des Avatars in `stepSimulation`.
   */
  pushMargin: 0.001,
  /** So oft wird je Zielwahl neu gehasht, wenn der Kandidat in einem festen Block liegt. */
  targetAttempts: 8,
} as const;

type SpeciesTable = Readonly<Record<string, Species>>;

/**
 * Ein neues Tier am Fußpunkt des Spawns: stehend, ohne Ziel, Blick nach Norden, Produkt voll
 * (`productReadyTick = tick`), Stehzeit aus dem Hash. Eine unbekannte Art ist ein Fehler
 * (ADR 0003); `table` ist standardmäßig SPECIES, Tests geben eigene Arten mit.
 */
export function createAnimal(spawn: AnimalSpawn, tick: number, table: SpeciesTable = SPECIES): AnimalState {
  getSpecies(spawn.species, table);
  return {
    id: spawn.id,
    species: spawn.species,
    pos: { x: spawn.x, y: spawn.y, z: spawn.z },
    yaw: 0,
    target: null,
    idleTicks: stehzeit(spawn.id, tick),
    penId: spawn.penId,
    productReadyTick: tick,
    moving: false,
    blockedTicks: 0,
  };
}

/** Ob das Produkt des Tiers bei `tick` bereit ist. */
export function isProductReady(animal: AnimalState, tick: number): boolean {
  return tick >= animal.productReadyTick;
}

/** Der Kollisionsquader des Tiers: halbe Breite der Art um den Fußpunkt, Höhe der Art. */
export function animalAabb(animal: AnimalState, table: SpeciesTable = SPECIES): Aabb {
  const species = getSpecies(animal.species, table);
  return aabbAt(animal.pos, species.width / 2, species.height);
}

/** Der Kollisionsquader des Avatars am Fußpunkt, mit Breite und Höhe aus PLAYER. */
function avatarAabb(player: PlayerState): Aabb {
  return aabbAt(player.pos, PLAYER.width / 2, PLAYER.height);
}

/**
 * Was ein Tierschritt aus dem Spielzustand braucht: den Takt und – solange keine Avatar-Quader
 * mitkommen – den Avatar als Hindernis. Ein `GameState` passt darauf; der Server hat je Hof
 * keinen, sondern reicht Takt und Anwesende herein (ADR 0027).
 */
export interface AnimalStepState {
  tick: number;
  player?: PlayerState;
}

/**
 * Genau ein Tick für ein Tier; verändert `animal`. Reihenfolge: stehen oder Ziel wählen, gehen
 * mit Kollision (Blöcke und Avatare) und Koppelgrenze, am Boden halten, vor einem Avatar
 * ausweichen, der in das Tier hineingelaufen ist. `pen` muss die Koppel zu `animal.penId` sein.
 *
 * `avatare` sind die Quader, die als Avatar zählen. Ohne die Liste ist es der eine aus
 * `state.player` (so rechnet der Client); der Server gibt die Quader **aller** Anwesenden auf
 * diesem Hof mit, und eine leere Liste heißt: niemand ist da.
 */
export function stepAnimal(
  animal: AnimalState,
  state: AnimalStepState,
  world: World,
  pen: Pen,
  table: SpeciesTable = SPECIES,
  avatare?: readonly Aabb[],
): void {
  if (pen.id !== animal.penId) {
    throw new Error(`Tier „${animal.id}“ gehört zur Koppel „${animal.penId}“, bekam aber die Koppel „${pen.id}“.`);
  }
  const species = getSpecies(animal.species, table);
  const hindernisse = avatare ?? (state.player === undefined ? [] : [avatarAabb(state.player)]);

  animal.moving = false;
  if (animal.target === null) {
    if (animal.idleTicks > 0) {
      animal.idleTicks -= 1;
    } else {
      neuesZiel(animal, state.tick, world, pen, species);
    }
  }
  if (animal.target !== null) {
    gehen(animal, animal.target, state.tick, world, pen, species, hindernisse);
  }

  // Schwerkraft: Die Koppel ist eben, das Tier steht auf ihrer Fußhöhe (ADR 0008).
  animal.pos.y = pen.y;
  vorAvatarAusweichen(animal, state.tick, world, pen, species, hindernisse);
}

/**
 * Ein Schritt auf das Ziel zu; kümmert sich um Ankunft und Blockade. Ein Avatar ist dabei ein
 * Hindernis wie ein fester Block: Das Tier läuft nicht in ihn hinein, sondern bleibt davor stehen
 * und gibt das Ziel nach der Blockade-Regel auf (ADR 0018).
 */
function gehen(animal: AnimalState, ziel: Vec3, tick: number, world: World, pen: Pen, species: Species, avatare: readonly Aabb[]): void {
  const hw = species.width / 2;
  const dx = ziel.x - animal.pos.x;
  const dz = ziel.z - animal.pos.z;
  const abstand = Math.hypot(dx, dz);
  if (abstand < ANIMAL_BEHAVIOR.arrivalDistance) {
    ankommen(animal, tick);
    return;
  }

  const schritt = Math.min(species.walkSpeed * TICK_SECONDS, abstand);
  animal.yaw = stetigerYaw(animal.yaw, Math.atan2(-dx, -dz));

  const vorher = animal.pos;
  const delta: Vec3 = { x: (dx / abstand) * schritt, y: 0, z: (dz / abstand) * schritt };
  animal.pos = moveAabb(world, vorher, hw, species.height, delta, avatare).pos;
  inKoppelHalten(animal.pos, pen, hw);
  animal.moving = animal.pos.x !== vorher.x || animal.pos.z !== vorher.z;

  const restAbstand = Math.hypot(ziel.x - animal.pos.x, ziel.z - animal.pos.z);
  const fortschritt = abstand - restAbstand;
  if (fortschritt < schritt * ANIMAL_BEHAVIOR.blockedProgressFraction) {
    animal.blockedTicks += 1;
  } else {
    animal.blockedTicks = 0;
  }

  if (animal.blockedTicks > ANIMAL_BEHAVIOR.blockedSeconds * TICK_RATE) {
    neuesZiel(animal, tick, world, pen, species);
  } else if (restAbstand < ANIMAL_BEHAVIOR.arrivalDistance) {
    ankommen(animal, tick);
  }
}

/** Ziel erreicht: stehen bleiben, neue Stehzeit aus dem Hash. */
function ankommen(animal: AnimalState, tick: number): void {
  animal.target = null;
  animal.blockedTicks = 0;
  animal.idleTicks = stehzeit(animal.id, tick);
}

/**
 * Wählt ein Ziel in der um die halbe Breite eingerückten Koppel aus dem Hash von id und Tick.
 * Liegt der Kandidat in einem festen Block (Stallwand), wird mit anderem Salz neu gehasht; nach
 * `targetAttempts` Versuchen gilt der letzte Kandidat, die Blockade-Regel fängt ihn dann ab.
 */
function neuesZiel(animal: AnimalState, tick: number, world: World, pen: Pen, species: Species): void {
  const bereich = koppelBereich(pen, species.width / 2);
  let ziel: Vec3 = { x: bereich.minX, y: pen.y, z: bereich.minZ };
  for (let versuch = 0; versuch < ANIMAL_BEHAVIOR.targetAttempts; versuch++) {
    const ux = hashEinheit(animal.id, tick, versuch * 2 + 1);
    const uz = hashEinheit(animal.id, tick, versuch * 2 + 2);
    ziel = {
      x: bereich.minX + ux * (bereich.maxX - bereich.minX),
      y: pen.y,
      z: bereich.minZ + uz * (bereich.maxZ - bereich.minZ),
    };
    if (!isSolid(world, ziel.x, pen.y, ziel.z)) break;
  }
  animal.target = ziel;
  animal.idleTicks = 0;
  animal.blockedTicks = 0;
}

/**
 * Ist ein Avatar in das Tier hineingelaufen (die Quader überlappen), weicht das Tier aus: nur
 * entlang der Achse mit der geringsten Durchdringung und nur vom Avatar weg, nie auf seine andere
 * Seite, über moveAabb (feste Blöcke stoppen) und die Koppelgrenze. Kommt es dabei nicht ganz
 * heraus (zwischen Avatar und Wand eingeklemmt), bleibt die Restüberlappung stehen und
 * `stepSimulation` hält den Avatar an (ADR 0018). Senkrecht weicht das Tier nicht aus; ein Avatar,
 * der von oben kommt, wird von der Simulation auf dem Rücken gehalten. Nach jedem waagerechten
 * Ausweichen wählt das Tier ein neues Ziel.
 */
function vorAvatarAusweichen(animal: AnimalState, tick: number, world: World, pen: Pen, species: Species, avatare: readonly Aabb[]): void {
  const hw = species.width / 2;
  for (const avatar of avatare) {
    const tier = aabbAt(animal.pos, hw, species.height);
    if (!aabbsOverlap(tier, avatar)) continue;

    const delta = separationDelta(tier, avatar, ANIMAL_BEHAVIOR.pushMargin);
    if (delta.y !== 0) continue;

    const pos = moveAabb(world, animal.pos, hw, species.height, delta).pos;
    pos.y = pen.y;
    inKoppelHalten(pos, pen, hw);
    animal.pos = pos;
    neuesZiel(animal, tick, world, pen, species);
  }
}

/** Der Bereich, in dem der Fußpunkt liegen darf: die Koppel, um die halbe Breite eingerückt. */
function koppelBereich(pen: Pen, hw: number): { minX: number; maxX: number; minZ: number; maxZ: number } {
  let minX = pen.minX + hw;
  let maxX = pen.maxX + 1 - hw;
  let minZ = pen.minZ + hw;
  let maxZ = pen.maxZ + 1 - hw;
  // Eine Koppel, die schmaler ist als das Tier, hält es in ihrer Mitte.
  if (minX > maxX) minX = maxX = (pen.minX + pen.maxX + 1) / 2;
  if (minZ > maxZ) minZ = maxZ = (pen.minZ + pen.maxZ + 1) / 2;
  return { minX, maxX, minZ, maxZ };
}

/** Begrenzt den Fußpunkt auf die Koppel; verändert `pos`. */
function inKoppelHalten(pos: Vec3, pen: Pen, hw: number): void {
  const bereich = koppelBereich(pen, hw);
  pos.x = clamp(pos.x, bereich.minX, bereich.maxX);
  pos.z = clamp(pos.z, bereich.minZ, bereich.maxZ);
}

/**
 * Der neue Blickwinkel, ausgedrückt als kürzeste Drehung ab dem bisherigen: Wie beim Spieler
 * wird `yaw` nicht normiert, damit der Client zwischen zwei Ticks ohne Sprung um 2π drehen kann.
 */
function stetigerYaw(vorher: number, ziel: number): number {
  const diff = ziel - vorher;
  return vorher + diff - 2 * Math.PI * Math.round(diff / (2 * Math.PI));
}

/** Stehzeit in ganzen Ticks, gleichverteilt zwischen idleMinSeconds und idleMaxSeconds. */
function stehzeit(id: string, tick: number): number {
  const min = ANIMAL_BEHAVIOR.idleMinSeconds * TICK_RATE;
  const max = ANIMAL_BEHAVIOR.idleMaxSeconds * TICK_RATE;
  return min + Math.floor(hashEinheit(id, tick, 0) * (max - min + 1));
}

/**
 * Deterministischer Hash aus Tier-id, Tick und Salz als Zahl in [0, 1): FNV-1a über die Zeichen
 * der id und die Bytes von Tick und Salz, danach eine Durchmischung, damit benachbarte Ticks nicht
 * benachbarte Werte ergeben. Kein Zufall zur Laufzeit (task-006).
 */
function hashEinheit(id: string, tick: number, salz: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  for (const wert of [tick, salz]) {
    for (let b = 0; b < 4; b++) {
      h ^= (wert >>> (b * 8)) & 0xff;
      h = Math.imul(h, 0x01000193);
    }
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
